import React, { useState } from "react";
import * as m from "../../lib/model";
import { capitalApi } from "../../lib/api";
import { Card } from "../shared";
import { toast } from "sonner";
import { Mail, Sparkles, Plus, X } from "lucide-react";

export const LeadDetail = ({ lead, today, onChange, onClose }) => {
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");

  const logNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try { onChange(await capitalApi.activity(lead.id, { kind: "Note", note })); setNote(""); toast.success("Logged"); }
    finally { setBusy(""); }
  };
  const makeDraft = async () => {
    setBusy("draft");
    try { setDraft(await capitalApi.draftEmail(lead.id)); }
    catch { toast.error("Draft failed"); }
    finally { setBusy(""); }
  };
  const send = async () => {
    setBusy("send");
    try {
      const r = await capitalApi.sendEmail(lead.id, draft);
      toast[r.delivered ? "success" : "info"](r.delivered ? `Sent to ${lead.email}` : "Email logged (Resend key missing)");
      setDraft(null);
      onChange(await capitalApi.patch(lead.id, { lastContact: today }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(""); }
  };

  return (
    <Card
      title={`${lead.name}${lead.firm ? ` · ${lead.firm}` : ""}`}
      style={{ marginTop: 12 }}
      testid={`lead-detail-${lead.id}`}
      right={<button className="btn" onClick={onClose} data-testid="lead-detail-close"><X size={13} />Close</button>}
    >
      <div className="cm-detail-grid">
        <div>
          <div className="section-label">LP file</div>
          <div className="cm-kv"><span>Email</span><b>{lead.email || "—"}</b></div>
          <div className="cm-kv"><span>Phone</span><b>{lead.phone || "—"}</b></div>
          <div className="cm-kv"><span>Type</span><b>{lead.type || "—"}</b></div>
          <div className="cm-kv"><span>Entity for docs</span><b>{lead.entityName || "—"}</b></div>
          <div className="cm-kv"><span>Ticket range</span><b>{lead.minTicket || lead.maxTicket ? `${m.fmt$(lead.minTicket)} – ${m.fmt$(lead.maxTicket)}` : "—"}</b></div>
          <div className="cm-kv"><span>Asked for</span><b>{m.fmt$(lead.askAmount)}</b></div>
          <div className="cm-kv"><span>Funded</span><b>{m.fmt$(lead.funded)}{lead.wireDate ? ` · ${lead.wireDate}` : ""}</b></div>
          <div className="cm-kv"><span>Class pref</span><b>{lead.classPref || "—"}</b></div>
          <div className="cm-kv"><span>Accreditation</span><b>{lead.accredited || "Unverified"}</b></div>
          <div className="cm-kv"><span>Docs</span><b>{lead.docsStatus || "None"}</b></div>
          <div className="cm-kv"><span>Intro source</span><b>{lead.source || "—"}</b></div>
          <div className="cm-kv"><span>Referred by</span><b>{lead.referrer || "—"}</b></div>
          <div className="cm-kv"><span>Last contact</span><b>{lead.lastContact || "—"}</b></div>
          <div className="cm-kv"><span>Owner</span><b>{lead.owner || "—"}</b></div>
          {lead.notes && <div className="cm-notes">{lead.notes}</div>}
        </div>
        <div>
          <div className="section-label">Activity</div>
          <div className="cm-log">
            {(lead.activities || []).length === 0 && <div className="cm-empty" style={{ padding: 10 }}>Nothing logged yet.</div>}
            {[...(lead.activities || [])].reverse().map((a) => (
              <div key={a.id} className="cm-log-row">
                <span className="cl-kind">{a.kind}</span>
                <span className="cl-note">{a.note}</span>
                <span className="cl-at">{(a.at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
          <div className="cm-note-add">
            <input className="inp" placeholder="Log a call / meeting note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && logNote()} data-testid={`lead-note-input-${lead.id}`} />
            <button className="btn" onClick={logNote} disabled={busy === "note" || !note.trim()} data-testid={`lead-note-add-${lead.id}`}><Plus size={13} />Log</button>
          </div>
          <div className="cm-email">
            <button className="btn" onClick={makeDraft} disabled={busy === "draft" || !lead.email} data-testid={`lead-draft-${lead.id}`}>
              <Sparkles size={13} />{busy === "draft" ? "Drafting…" : "AI follow-up draft"}
            </button>
            {!lead.email && <span className="cm-empty">Add an email to send follow-ups.</span>}
          </div>
          {draft && (
            <div className="cm-draft" data-testid={`lead-draft-box-${lead.id}`}>
              <input className="inp inp-text" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid={`lead-draft-subject-${lead.id}`} />
              <textarea className="inp" rows={7} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid={`lead-draft-body-${lead.id}`} />
              <div className="cm-draft-acts">
                <button className="btn" onClick={() => setDraft(null)}>Discard</button>
                <button className="btn btn-primary" onClick={send} disabled={busy === "send"} data-testid={`lead-send-${lead.id}`}><Mail size={13} />{busy === "send" ? "Sending…" : `Send to ${lead.email}`}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
